/**
 * "Open the interview link for this stage" — the way out of a booking problem.
 *
 * Every place the board complains about a booking URL is a place the recruiter
 * then has to FIND the link that caused it: the right role, the right stage,
 * among every link the team owns. The complaint and the fix were on two
 * different pages with no thread between them, so the advice ("set one up on
 * the Interview Links page") was a sentence rather than a route.
 *
 * Three decisions worth keeping:
 *
 * 1. **Rendered whether or not a link exists**, unlike EmailLibraryLink, which
 *    hides itself without a key. Absent link IS the most common reason to go
 *    there — to create one — so the no-link case gets `?posting=…&stage=…` and
 *    the create form opens pre-filled. A dead end only exists when we know
 *    neither, which is why `postingId` is required.
 * 2. **A NEW TAB, always.** Every caller is either a modal holding unsaved
 *    per-batch edits or an accordion the recruiter has arranged; a same-tab hop
 *    to check one link throws that away. Same reasoning as EmailLibraryLink.
 * 3. **The stage travels even when the link id is known**, so a `?link=` that no
 *    longer resolves (deleted, or a stale cached board) still lands on the
 *    create form for the right role and stage instead of an unfiltered list.
 */
import { ExternalLink } from 'lucide-react';

export function FixInterviewLink({
  linkId,
  postingId,
  stageId,
  stageName,
  label,
  className = 'inline-flex items-center gap-1 text-xs text-blue-600 hover:underline',
}: {
  /** The interview link governing this stage, when the board found one. */
  linkId?: string | null;
  /** Required — without it there is nothing to open, so the link is not rendered. */
  postingId?: string | null;
  stageId?: string | null;
  stageName?: string | null;
  label?: string;
  className?: string;
}) {
  const posting = `${postingId || ''}`.trim();
  const id = `${linkId || ''}`.trim();
  if (!posting && !id) return null;

  const params = new URLSearchParams();
  if (id) params.set('link', id);
  if (posting) params.set('posting', posting);
  if (stageId) params.set('stage', `${stageId}`);

  const text = label || (id ? 'Open the interview link' : 'Set up the interview link');
  const where = stageName ? ` for ${stageName}` : '';
  return (
    <a
      href={`/interview-links?${params.toString()}`}
      target="_blank"
      rel="noreferrer"
      // The modals and accordions that host this all toggle on click somewhere up the tree.
      data-no-toggle
      onClick={(e) => e.stopPropagation()}
      className={className}
      title={
        id
          ? `Opens the Interview Links page in a new tab, on the link${where}. Your edits here are not lost.`
          : `Opens the Interview Links page in a new tab, ready to create the link${where}. Your edits here are not lost.`
      }
    >
      <ExternalLink className="h-3 w-3" /> {text}
    </a>
  );
}
